import { NestedHome } from '@habity/types';
import { isCommercialProperty } from '@habity/types/vitec/assertions';
import { CommercialPropertyInfoEstateCompilationAreas } from '@habity/types/vitec/common/commercialPropertyInfoEstateCompilationAreas';
import { CommercialPropertyInfoEstateCompilationArea } from '@habity/types/vitec/common/commercialPropertyInfoEstateCompilationArea';
import { DataFieldConfig } from './data-builder';
import { ICONS } from '~/constants/icons';
import { formatCurrency } from '@habity/utils';

export const getCommercialAreasFromHome = (context: { home: NestedHome }) => {
  const { home } = context;
  const { provider_data, provider_type } = home || {};
  if (!provider_data || !provider_type) return null;

  if (isCommercialProperty(provider_data, provider_type)) {
    // @ts-ignore
    const { compilationAreas } = provider_data || {};
    return compilationAreas;
  }

  return null;
};

export const hasCompilationAreasData = (
  compilationAreas:
    | CommercialPropertyInfoEstateCompilationAreas
    | null
    | undefined
): boolean => {
  if (!compilationAreas) return false;

  const areaTypes = [
    'residential',
    'garage',
    'parkingLot',
    'office',
    'stores',
    'industry',
    'warmStockroom',
    'coldStockroom',
    'other1',
    'other2',
  ];

  return areaTypes.some((areaType) => {
    const areaData = compilationAreas[
      areaType as keyof typeof compilationAreas
    ] as CommercialPropertyInfoEstateCompilationArea;
    return (
      areaData &&
      (areaData.number ||
        areaData.area ||
        areaData.rentalIncome ||
        areaData.otherIncome)
    );
  });
};

const getSwedishAreaName = (areaType: string): string => {
  const areaNames: Record<string, string> = {
    residential: 'Bostäder',
    garage: 'Garage',
    parkingLot: 'Parkeringsplats',
    office: 'Kontor',
    stores: 'Butiker',
    industry: 'Industri',
    warmStockroom: 'Varmt lager',
    coldStockroom: 'Kallt lager',
    other1: 'Övrigt 1',
    other2: 'Övrigt 2',
  };
  return areaNames[areaType] || areaType;
};

export const createCommercialAreasFields = (context: {
  home: NestedHome;
}): DataFieldConfig[] => {
  const compilationAreas = getCommercialAreasFromHome(context);
  if (!compilationAreas) return [];

  const fields: DataFieldConfig[] = [];
  const areaTypes = [
    'residential',
    'garage',
    'parkingLot',
    'office',
    'stores',
    'industry',
    'warmStockroom',
    'coldStockroom',
    'other1',
    'other2',
  ];

  areaTypes.forEach((areaType) => {
    const areaData = compilationAreas[
      areaType as keyof typeof compilationAreas
    ] as CommercialPropertyInfoEstateCompilationArea;

    if (
      areaData &&
      (areaData.number ||
        areaData.area ||
        areaData.rentalIncome ||
        areaData.otherIncome)
    ) {
      const areaName = getSwedishAreaName(areaType);

      // Add number field if exists
      if (areaData.number) {
        fields.push({
          key: `compilationAreas.${areaType}.number`,
          label: `${areaName} - Antal`,
          icon: ICONS.BUILDING.CATEGORY,
          formatter: (value) => `${value} st`,
        });
      }

      // Add area field if exists
      if (areaData.area) {
        fields.push({
          key: `compilationAreas.${areaType}.area`,
          label: `${areaName} - Yta`,
          icon: ICONS.LIVING_SPACE,
          formatter: (value) => `${value.toLocaleString('sv-SE')} kvm`,
        });
      }

      // Add rental income field if exists
      if (areaData.rentalIncome) {
        fields.push({
          key: `compilationAreas.${areaType}.rentalIncome`,
          label: `${areaName} - Hyresintäkter`,
          icon: ICONS.PRICE,
          formatter: (value) => formatCurrency(value),
        });
      }

      // Add other income field if exists
      if (areaData.otherIncome) {
        fields.push({
          key: `compilationAreas.${areaType}.otherIncome`,
          label: `${areaName} - Övriga intäkter`,
          icon: ICONS.PRICE,
          formatter: (value) => formatCurrency(value),
        });
      }
    }
  });

  return fields;
};
